import { ALL_PRODUCT_IDS } from '../subscriptions/catalog';
import type { ProductId } from '../subscriptions/types';
import { loadNativeWebViewInsets, parseNativeAppOS } from './nativeWebViewInsets';
import { isNativeWebView } from './nativeAuthBridge';

export const NATIVE_APP_RESUMED_MESSAGE_TYPE = 'NATIVE_APP_RESUMED';
export const NATIVE_IAP_PURCHASE_UPDATE_MESSAGE_TYPE = 'NATIVE_IAP_PURCHASE_UPDATE';

const PENDING_SESSION_ID_KEY = 'pendingCheckoutSessionId';
const PENDING_PRODUCT_ID_KEY = 'pendingCheckoutProductId';
const PENDING_ASSIGNMENT_ID_KEY = 'pendingCheckoutAssignmentId';
const PENDING_STARTED_AT_KEY = 'pendingCheckoutStartedAt';
const PENDING_CHECKOUT_METHOD_KEY = 'pendingCheckoutMethod';
const PENDING_APP_ACCOUNT_TOKEN_KEY = 'pendingCheckoutAppAccountToken';
const PENDING_IAP_PURCHASE_ID_KEY = 'pendingCheckoutIapPurchaseId';
const PENDING_SIGNED_TRANSACTION_KEY = 'pendingCheckoutSignedTransaction';

export type CheckoutMethod = 'stripe' | 'iap';

export type PendingCheckoutContext = {
  sessionId?: string;
  productId: ProductId;
  assignmentId?: number;
  startedAt: number;
  checkoutMethod: CheckoutMethod;
  appAccountToken?: string;
  iapPurchaseId?: string;
  signedTransaction?: string;
};

type NativeAppResumedListener = () => void;
type NativeIapPurchaseUpdateListener = (detail: NativeIapPurchaseUpdate) => void;

const resumedListeners = new Set<NativeAppResumedListener>();
const iapPurchaseListeners = new Set<NativeIapPurchaseUpdateListener>();
let messageListenerRegistered = false;

export type ExternalPurchaseOpenStatus =
  | 'openedStoreKitLink'
  | 'openedFallbackBrowser'
  | 'unavailable'
  | 'cancelled'
  | 'failed';

export type ExternalPurchaseOpenResult = {
  status: ExternalPurchaseOpenStatus;
  usedStoreKit?: boolean;
  message?: string;
};

export type NativeIapPurchaseStatus = 'purchased' | 'cancelled' | 'pending' | 'failed' | 'restored';

export type NativeIapPurchaseResult = {
  status: NativeIapPurchaseStatus;
  purchaseId?: string;
  appleProductId?: string;
  signedTransaction?: string;
  message?: string;
};

export type NativeIapPurchaseUpdate = {
  type: typeof NATIVE_IAP_PURCHASE_UPDATE_MESSAGE_TYPE;
  status?: NativeIapPurchaseStatus;
  purchaseId?: string;
  appleProductId?: string;
  signedTransaction?: string;
};

export function isIosNativeApp(): boolean {
  if (!isNativeWebView()) {
    return false;
  }
  const appOS = loadNativeWebViewInsets().appOS ?? readLocalAppOS();
  return appOS === 'ios';
}

function readLocalAppOS() {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    return parseNativeAppOS(window.localStorage.getItem('appOS'));
  } catch {
    return undefined;
  }
}

function parseProductId(value: string | null): ProductId | null {
  if (!value || !ALL_PRODUCT_IDS.includes(value as ProductId)) {
    return null;
  }
  return value as ProductId;
}

function parseCheckoutMethod(value: string | null): CheckoutMethod {
  return value === 'iap' ? 'iap' : 'stripe';
}

export function savePendingCheckout(context: {
  sessionId?: string;
  productId: ProductId;
  assignmentId?: number;
  checkoutMethod?: CheckoutMethod;
  appAccountToken?: string;
  iapPurchaseId?: string;
  signedTransaction?: string;
}): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (context.sessionId) {
      window.localStorage.setItem(PENDING_SESSION_ID_KEY, context.sessionId);
    } else {
      window.localStorage.removeItem(PENDING_SESSION_ID_KEY);
    }
    window.localStorage.setItem(PENDING_PRODUCT_ID_KEY, context.productId);
    if (context.assignmentId !== undefined) {
      window.localStorage.setItem(PENDING_ASSIGNMENT_ID_KEY, String(context.assignmentId));
    } else {
      window.localStorage.removeItem(PENDING_ASSIGNMENT_ID_KEY);
    }
    window.localStorage.setItem(
      PENDING_CHECKOUT_METHOD_KEY,
      context.checkoutMethod ?? 'stripe',
    );
    if (context.appAccountToken) {
      window.localStorage.setItem(PENDING_APP_ACCOUNT_TOKEN_KEY, context.appAccountToken);
    } else {
      window.localStorage.removeItem(PENDING_APP_ACCOUNT_TOKEN_KEY);
    }
    if (context.iapPurchaseId) {
      window.localStorage.setItem(PENDING_IAP_PURCHASE_ID_KEY, context.iapPurchaseId);
    } else {
      window.localStorage.removeItem(PENDING_IAP_PURCHASE_ID_KEY);
    }
    if (context.signedTransaction) {
      window.localStorage.setItem(PENDING_SIGNED_TRANSACTION_KEY, context.signedTransaction);
    } else {
      window.localStorage.removeItem(PENDING_SIGNED_TRANSACTION_KEY);
    }
    window.localStorage.setItem(PENDING_STARTED_AT_KEY, String(Date.now()));
  } catch {
    // ignore storage failures
  }
}

export function readPendingCheckout(): PendingCheckoutContext | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const sessionId = window.localStorage.getItem(PENDING_SESSION_ID_KEY) ?? undefined;
    const productId = parseProductId(window.localStorage.getItem(PENDING_PRODUCT_ID_KEY));
    const startedAtRaw = window.localStorage.getItem(PENDING_STARTED_AT_KEY);
    if (!productId || !startedAtRaw) {
      return null;
    }
    const assignmentRaw = window.localStorage.getItem(PENDING_ASSIGNMENT_ID_KEY);
    const assignmentId =
      assignmentRaw && assignmentRaw !== '' ? parseInt(assignmentRaw, 10) : undefined;
    const startedAt = parseInt(startedAtRaw, 10);
    if (Number.isNaN(startedAt)) {
      return null;
    }
    const checkoutMethod = parseCheckoutMethod(
      window.localStorage.getItem(PENDING_CHECKOUT_METHOD_KEY),
    );
    const appAccountToken =
      window.localStorage.getItem(PENDING_APP_ACCOUNT_TOKEN_KEY) ?? undefined;
    const iapPurchaseId = window.localStorage.getItem(PENDING_IAP_PURCHASE_ID_KEY) ?? undefined;
    const signedTransaction =
      window.localStorage.getItem(PENDING_SIGNED_TRANSACTION_KEY) ?? undefined;

    if (checkoutMethod === 'iap') {
      if (!signedTransaction && !appAccountToken) {
        return null;
      }
    } else if (!sessionId) {
      return null;
    }

    return {
      ...(sessionId ? { sessionId } : {}),
      productId,
      ...(assignmentId !== undefined && !Number.isNaN(assignmentId) ? { assignmentId } : {}),
      startedAt,
      checkoutMethod,
      ...(appAccountToken ? { appAccountToken } : {}),
      ...(iapPurchaseId ? { iapPurchaseId } : {}),
      ...(signedTransaction ? { signedTransaction } : {}),
    };
  } catch {
    return null;
  }
}

export function clearPendingCheckout(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_SESSION_ID_KEY);
    window.localStorage.removeItem(PENDING_PRODUCT_ID_KEY);
    window.localStorage.removeItem(PENDING_ASSIGNMENT_ID_KEY);
    window.localStorage.removeItem(PENDING_STARTED_AT_KEY);
    window.localStorage.removeItem(PENDING_CHECKOUT_METHOD_KEY);
    window.localStorage.removeItem(PENDING_APP_ACCOUNT_TOKEN_KEY);
    window.localStorage.removeItem(PENDING_IAP_PURCHASE_ID_KEY);
    window.localStorage.removeItem(PENDING_SIGNED_TRANSACTION_KEY);
  } catch {
    // ignore storage failures
  }
}

export function subscribeNativeAppResumed(listener: NativeAppResumedListener): () => void {
  resumedListeners.add(listener);
  return () => {
    resumedListeners.delete(listener);
  };
}

export function subscribeNativeIapPurchaseUpdates(
  listener: NativeIapPurchaseUpdateListener,
): () => void {
  iapPurchaseListeners.add(listener);
  return () => {
    iapPurchaseListeners.delete(listener);
  };
}

function notifyNativeAppResumed(): void {
  resumedListeners.forEach((listener) => listener());
}

function notifyNativeIapPurchaseUpdate(detail: NativeIapPurchaseUpdate): void {
  iapPurchaseListeners.forEach((listener) => listener(detail));
}

function onWindowMessage(event: MessageEvent): void {
  const data = event.data;
  if (!data || typeof data !== 'object') {
    return;
  }
  const type = (data as { type?: string }).type;
  if (type === NATIVE_APP_RESUMED_MESSAGE_TYPE) {
    notifyNativeAppResumed();
    return;
  }
  if (type === NATIVE_IAP_PURCHASE_UPDATE_MESSAGE_TYPE) {
    notifyNativeIapPurchaseUpdate(data as NativeIapPurchaseUpdate);
  }
}

export function bootstrapNativeCheckoutBridge(): void {
  if (typeof window === 'undefined' || messageListenerRegistered) {
    return;
  }
  window.addEventListener('message', onWindowMessage);
  messageListenerRegistered = true;
}

function normalizeNativeIapPurchaseResult(result: unknown): NativeIapPurchaseResult {
  if (!result || typeof result !== 'object') {
    return { status: 'failed' };
  }
  const status = (result as { status?: string }).status;
  if (
    status === 'purchased' ||
    status === 'cancelled' ||
    status === 'pending' ||
    status === 'failed' ||
    status === 'restored'
  ) {
    return {
      status,
      purchaseId:
        typeof (result as { purchaseId?: string }).purchaseId === 'string'
          ? (result as { purchaseId?: string }).purchaseId
          : undefined,
      appleProductId:
        typeof (result as { appleProductId?: string }).appleProductId === 'string'
          ? (result as { appleProductId?: string }).appleProductId
          : undefined,
      signedTransaction:
        typeof (result as { signedTransaction?: string }).signedTransaction === 'string'
          ? (result as { signedTransaction?: string }).signedTransaction
          : undefined,
      message:
        typeof (result as { message?: string }).message === 'string'
          ? (result as { message?: string }).message
          : undefined,
    };
  }
  return { status: 'failed' };
}

export async function startNativeIapPurchase(params: {
  appleProductId: string;
  appAccountToken: string;
}): Promise<NativeIapPurchaseResult> {
  if (window.flutter_inappwebview?.callHandler) {
    const result = await window.flutter_inappwebview.callHandler(
      'startNativeIapPurchase',
      params,
    );
    return normalizeNativeIapPurchaseResult(result);
  }
  return { status: 'failed', message: 'Native IAP bridge unavailable' };
}

export async function finishNativeIapPurchase(purchaseId: string): Promise<boolean> {
  if (window.flutter_inappwebview?.callHandler) {
    const result = await window.flutter_inappwebview.callHandler('finishNativeIapPurchase', {
      purchaseId,
    });
    return Boolean(result && typeof result === 'object' && (result as { status?: string }).status === 'completed');
  }
  return false;
}

export type RestoredNativePurchase = {
  purchaseId?: string;
  appleProductId?: string;
  signedTransaction?: string;
  status?: string;
};

export async function restoreNativePurchases(): Promise<RestoredNativePurchase[]> {
  if (window.flutter_inappwebview?.callHandler) {
    const result = await window.flutter_inappwebview.callHandler('restoreNativePurchases');
    if (!Array.isArray(result)) {
      return [];
    }
    return result.filter((item) => item && typeof item === 'object') as RestoredNativePurchase[];
  }
  return [];
}

export async function canOpenStoreKitExternalPurchase(): Promise<boolean> {
  if (window.flutter_inappwebview?.callHandler) {
    const result = await window.flutter_inappwebview.callHandler(
      'canOpenStoreKitExternalPurchase',
    );
    return Boolean(result);
  }
  return false;
}

function normalizeExternalPurchaseResult(
  result: unknown,
): ExternalPurchaseOpenResult {
  if (!result || typeof result !== 'object') {
    return { status: 'failed' };
  }
  const status = (result as { status?: string }).status;
  if (
    status === 'openedStoreKitLink' ||
    status === 'openedFallbackBrowser' ||
    status === 'unavailable' ||
    status === 'cancelled' ||
    status === 'failed'
  ) {
    return {
      status,
      usedStoreKit: Boolean((result as { usedStoreKit?: boolean }).usedStoreKit),
      message:
        typeof (result as { message?: string }).message === 'string'
          ? (result as { message?: string }).message
          : undefined,
    };
  }
  return { status: 'failed' };
}

export async function openStoreKitExternalPurchase(
  checkoutUrl: string,
): Promise<ExternalPurchaseOpenResult> {
  if (window.flutter_inappwebview?.callHandler) {
    const result = await window.flutter_inappwebview.callHandler(
      'openStoreKitExternalPurchase',
      checkoutUrl,
    );
    return normalizeExternalPurchaseResult(result);
  }
  window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
  return { status: 'openedFallbackBrowser', usedStoreKit: false };
}

export async function startExternalCheckout(
  checkoutUrl: string,
): Promise<ExternalPurchaseOpenResult> {
  if (isIosNativeApp()) {
    return { status: 'unavailable', message: 'Use in-app purchase on iOS.' };
  }
  if (window.flutter_inappwebview?.callHandler) {
    await window.flutter_inappwebview.callHandler('startExternalCheckout', checkoutUrl);
    return { status: 'openedFallbackBrowser', usedStoreKit: false };
  }
  window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
  return { status: 'openedFallbackBrowser', usedStoreKit: false };
}
